import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, IconButton, Button, Tooltip, useMediaQuery } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import DashboardAppBar from './DashboardAppBar';
import InputForm from './InputForm';
import ResumePreview from './ResumePreview';
import ResumeLoadingOverlay from '../Common/ResumeLoadingOverlay';
import { saveResume, updateResume, getResumeById, getSubscriptionStatus } from '../../services/api';
import { mobileSegmentedControlSx, mobileSegmentButtonSx, dashboardShellBgSx } from '../Common/formStyles';

const EDITOR_STANDALONE_MAX = 1080;
const EDITOR_SPLIT_MAX = 480;
const LAYOUT_TRANSITION = (theme) =>
  theme.transitions.create(['width', 'max-width', 'margin', 'opacity', 'flex', 'flex-basis', 'transform'], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  });

const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [currentResume, setCurrentResume] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [mobileTab, setMobileTab] = useState(0);
  const [loadingResume, setLoadingResume] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({ isActive: false, subscription: null });
  const [editPanelCollapsed, setEditPanelCollapsed] = useState(() => {
    try {
      return localStorage.getItem('dashboard-edit-panel-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const triggerDownloadRef = useRef(null);

  const loadResume = useCallback(async (resumeId, { switchToPreview = false, afterLoad } = {}) => {
    setLoadingResume(true);
    try {
      const fullResume = await getResumeById(resumeId);
      setCurrentResume(fullResume);
      setResumeData(fullResume);
      if (switchToPreview && fullResume?.html) {
        setMobileTab(1);
      }
      afterLoad?.(fullResume);
      return fullResume;
    } catch (err) {
      console.error('Failed to load resume:', err);
      throw err;
    } finally {
      setLoadingResume(false);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('dashboard-edit-panel-collapsed', String(editPanelCollapsed));
    } catch {
      // ignore storage errors
    }
  }, [editPanelCollapsed]);

  useEffect(() => {
    getSubscriptionStatus()
      .then(setSubscriptionStatus)
      .catch((err) => console.error('Failed to fetch subscription status:', err));
  }, []);

  useEffect(() => {
    const pendingResumeId = sessionStorage.getItem('pendingDownload');
    if (!pendingResumeId) return;
    sessionStorage.removeItem('pendingDownload');

    loadResume(pendingResumeId, {
      switchToPreview: true,
      afterLoad: () => {
        setTimeout(() => {
          if (triggerDownloadRef.current) {
            triggerDownloadRef.current();
          }
        }, 800);
      },
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (!resumeId) return;

    loadResume(resumeId)
      .then(() => setSearchParams({}, { replace: true }))
      .catch(() => setSearchParams({}, { replace: true }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('resume')]);

  const handleResumeGenerated = async (data) => {
    setResumeData(data);
    if (data?.html && isMobile) {
      setMobileTab(1);
    }

    try {
      let savedResume;
      if (currentResume?.id) {
        savedResume = await updateResume(currentResume.id, { ...data, isAIGeneration: true });
      } else {
        savedResume = await saveResume({ ...data, aiGenerationCount: 1 });
      }
      setCurrentResume(savedResume);
    } catch (error) {
      if (error.response?.status === 402) {
        console.warn('AI generation limit reached on backend:', error.response.data);
      } else {
        console.error('Failed to save resume:', error);
      }
    }
  };

  const handleNewResume = () => {
    setCurrentResume(null);
    setResumeData(null);
    setMobileTab(0);
    setEditPanelCollapsed(false);
  };

  const handleContentChange = async (newContent) => {
    if (currentResume?.id) {
      try {
        await updateResume(currentResume.id, {
          ...resumeData,
          html: newContent,
        });
      } catch (error) {
        console.error('Failed to update resume:', error);
      }
    }
  };

  const currentTitle = currentResume?.title || resumeData?.title;
  const showPreview = Boolean(resumeData?.html);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <DashboardAppBar
        user={user}
        currentResumeTitle={currentTitle}
        onNewResume={handleNewResume}
      />

      <Box
        component="main"
        sx={{
          position: 'relative',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {loadingResume && <ResumeLoadingOverlay />}
        {isMobile && showPreview && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={mobileSegmentedControlSx}>
              {[
                { value: 0, label: 'Edit', icon: EditIcon },
                { value: 1, label: 'Preview', icon: PreviewIcon },
              ].map(({ value, label, icon: Icon }) => (
                <Button
                  key={value}
                  onClick={() => setMobileTab(value)}
                  startIcon={<Icon />}
                  sx={mobileSegmentButtonSx(mobileTab === value)}
                >
                  {label}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            minWidth: 0,
            position: 'relative',
            ...dashboardShellBgSx,
          }}
        >
          {/* Editor — centered when solo, slides left when preview opens */}
          <Box
            sx={{
              height: '100%',
              overflow: 'hidden',
              bgcolor: 'background.default',
              transition: LAYOUT_TRANSITION,
              display: {
                xs: !showPreview || mobileTab === 0 ? 'flex' : 'none',
                md: editPanelCollapsed && showPreview ? 'none' : 'flex',
              },
              flexDirection: 'column',
              width: {
                xs: '100%',
                md: showPreview ? '40%' : `min(calc(100% - 48px), ${EDITOR_STANDALONE_MAX}px)`,
              },
              maxWidth: {
                md: showPreview ? EDITOR_SPLIT_MAX : EDITOR_STANDALONE_MAX,
              },
              minWidth: { md: showPreview ? 320 : 0 },
              flexShrink: showPreview ? 0 : 1,
              ml: { md: showPreview ? 0 : 'auto' },
              mr: { md: showPreview ? 0 : 'auto' },
              borderRight: { md: showPreview ? 1 : 0 },
              borderColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'divider'),
            }}
          >
            <InputForm
              onResumeGenerated={handleResumeGenerated}
              initialData={resumeData}
              subscriptionStatus={subscriptionStatus}
              isSplitView={showPreview}
            />
          </Box>

          {/* Collapse rail — fades in with preview */}
          {!isMobile && (
            <Box
              sx={{
                flexShrink: 0,
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: showPreview ? 1 : 0,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                width: showPreview ? 28 : 0,
                opacity: showPreview ? 1 : 0,
                transition: LAYOUT_TRANSITION,
                pointerEvents: showPreview ? 'auto' : 'none',
              }}
            >
              <Tooltip title={editPanelCollapsed ? 'Show editor' : 'Hide editor'} placement="right">
                <IconButton
                  size="small"
                  onClick={() => setEditPanelCollapsed((prev) => !prev)}
                  aria-label={editPanelCollapsed ? 'Show editor panel' : 'Hide editor panel'}
                >
                  {editPanelCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Preview — expands in from the right */}
          <Box
            sx={{
              height: '100%',
              overflow: 'hidden',
              bgcolor: 'background.default',
              transition: LAYOUT_TRANSITION,
              display: {
                xs: showPreview && mobileTab === 1 ? 'block' : 'none',
                md: showPreview ? 'block' : 'none',
              },
              flex: showPreview ? 1 : 0,
              minWidth: 0,
              opacity: showPreview ? 1 : 0,
              transform: showPreview ? 'translateX(0)' : 'translateX(16px)',
            }}
          >
            {showPreview && (
              <ResumePreview
                resumeData={resumeData}
                onContentChange={handleContentChange}
                subscriptionStatus={subscriptionStatus}
                triggerDownloadRef={triggerDownloadRef}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
